import { Badge } from '@mantine/core';
import type { CSSProperties } from 'react';
import {
  getRunSourceLabel,
  getRunVersionLabel,
  getRunVersionStateLabel,
  Run,
  RunMode,
  RunOrigin,
} from '@/types/runs.ts';

type RunTypeBadgeProps = {
  run: Run;
  style?: CSSProperties;
};

type RunBadgeKind = 'version' | 'state' | 'source';

const getBadgeConfig = (run: Run, kind: RunBadgeKind): { label: string | null; color: string } => {
  if (kind === 'version') {
    return {
      label: getRunVersionLabel(run),
      color: 'gray',
    };
  }

  if (kind === 'state') {
    const label = getRunVersionStateLabel(run);
    return {
      label,
      color: label === 'Published' ? 'green' : label === 'Draft' ? 'blue' : 'gray',
    };
  }

  const label = getRunSourceLabel(run);
  const color =
    run.run_origin === RunOrigin.HealthCheck
      ? 'violet'
      : run.run_origin === RunOrigin.Builder && run.run_mode !== RunMode.Operational
        ? 'orange'
        : run.run_origin === RunOrigin.Portal
          ? 'grape'
          : run.run_origin === RunOrigin.ExternalTrigger
            ? 'teal'
            : run.run_origin === RunOrigin.Scheduled
              ? 'cyan'
            : 'gray';

  return { label, color };
};

const RunTypeBadge = ({ run, style }: RunTypeBadgeProps) => {
  const versionBadge = getBadgeConfig(run, 'version');
  const stateBadge = getBadgeConfig(run, 'state');
  const sourceBadge = getBadgeConfig(run, 'source');

  return (
    <>
      {versionBadge.label ? (
        <Badge
          size="xs"
          color={versionBadge.color}
          variant="light"
          style={{ flexShrink: 0, textTransform: 'none', ...style }}
        >
          {versionBadge.label}
        </Badge>
      ) : null}
      {stateBadge.label ? (
        <Badge
          size="xs"
          color={stateBadge.color}
          variant="light"
          style={{ flexShrink: 0, textTransform: 'none', ...style }}
        >
          {stateBadge.label}
        </Badge>
      ) : null}
      {sourceBadge.label ? (
        <Badge
          size="xs"
          color={sourceBadge.color}
          variant="light"
          style={{ flexShrink: 0, textTransform: 'none', ...style }}
        >
          {sourceBadge.label}
        </Badge>
      ) : null}
    </>
  );
};

export default RunTypeBadge;
